import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Card, Grid, Paper } from '@mui/material';
import { BakeasyChipEdit } from '../../../components/common/BakeasyChip';
import { AppContext } from '../../../context/AppContext';
import BakeasyTable from '../../../components/tables/BakeasyTable';
import BakeasyEmptyMsg from '../../../components/common/BakeasyEmptyMsg';
import BakeasySwitch from '../../../components/common/BakeasySwitch';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { STATUS_ALL_ARRAY1, UNAUTHORIZED } from '../../../constants/Constants';
import { ShowSnackbarFailure } from '../../../components/snackbars/BakeasySnackBar';
import { useAppDispatch, useAppSelector } from '../../../redux_store/reduxStore';
import {
  clearSubCategoryState,
  downloadSubCategories,
  fetchSubCategories,
  modifySubCategory,
  subCategoryState
} from '../../../redux_store/slices/subCategorySlice';
import { showErrorSnackbar } from '../../../redux_store/slices/snackbarSlice';
import { BakeasyAppBar } from '../../../components/common/BakeasyAppBar';
import { authActions } from '../../../redux_store/slices/authSlice';
import { BakeasyButtonAdd } from '../../../components/buttons/BakeasyButton';
import * as TextField from '../../../components/textfields/BakeasyTextFields';
import { BakeasySelect } from '../../../components/dropdowns/BakeasySelect';
import { clearProductState } from '../../../redux_store/slices/productsSlice';
import * as Button from '../../../components/buttons/BakeasyButtons';
import { subCategoriesTableHeaders } from '../../../constants/TableHeaders';
import { preparePayload } from '../../../utils/commonUtils';
import { useDebouncedFetch } from '../../../custom/useDebouncedCallback';

export const SubCategories: FC = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();

  let myContext = React.useContext(AppContext);

  const [subCategoryList, setSubCategoryList] = useState<any[]>([]);
  const [search, setSearch] = useState<string>('');
  const [count, setCount] = React.useState<number>(0);
  const tableRef = React.useRef<any>();
  const [categoryId, setCategoryId] = useState<string>(location?.state?.categoryId);

  let { error, value, rowsPerPage, pageNo, filter } = useAppSelector(subCategoryState);

  interface CategoryData {
    subCategoryName?: any;
    _id?: any;
    categoryId: any;
    subCategoryId?: string;
    name: string;
    description?: string;
    status: any;
  }

  useEffect(() => {
    if (error === UNAUTHORIZED) {
      dispatch(authActions.logout());
    }
  }, [error]);

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((subcategory: any) => {
        return {
          subCategoryName: subcategory?.subCategoryName,
          description: subcategory?.description,
          onSubCategoryNameClick: () => {
            dispatch(clearSubCategoryState());
            dispatch(clearProductState());
            navigate('/products', { state: { categoryId, subCategoryId: subcategory?._id } });
          },
          actions: [
            <BakeasyChipEdit
              key={subcategory?._id}
              onClick={(event: any) => {
                event.stopPropagation();
                openAddEditDialog(true, {
                  categoryId: subcategory?.categoryId,
                  subCategoryId: subcategory?._id,
                  name: subcategory?.subCategoryName,
                  description: subcategory?.description,
                  status: subcategory?.status
                });
              }}
            />
          ],
          status: (
            <BakeasySwitch
              value={subcategory?.status === 'Active' ? true : false}
              onChange={(event: any) => {
                event.stopPropagation();
                subcategory?.status === 'Active'
                  ? openDeActivateSupplierConfirmDialog(subcategory)
                  : openReActivateSupplierConfirmDialog(subcategory);
              }}
            />
          )
        };
      });

      setSubCategoryList(filteredData);
      setCount(value?.totalCount);

      if (filteredData?.length === 0) {
        dispatch(showErrorSnackbar({ message: t('NO_DATA_FOUND_SUB_FOR_CATEGORY') }));
      }
    }
  }, [value]);

  function openAddEditDialog(isEdit: any, data: CategoryData) {
    return myContext.setDialog({
      open: true,
      title: isEdit ? 'EDIT_SUB_CATEGORY' : 'ADD_SUB_CATEGORY',
      dialogContent: {
        pageName: 'AddEditSubCategory',
        params: { isEdit, ...data },
        onBack: async () => {
          await refreshPageData(pageNo - 1, search, filter);
        }
      }
    });
  }

  function openReActivateSupplierConfirmDialog(subcategory: CategoryData) {
    myContext.setConfirmDialog({
      type: 'reActivate',
      open: true,
      title: t('REACTIVATE_SUB_CATEGORY'),
      message: t('REACTIVATE_SUB_CATEGORY_MESSAGE'),
      onNoClick: () => { },
      onYesClick: (event: any, param: string | undefined) => {
        updateSubCategoryStatus(subcategory, 'Active');
      }
    });
  }

  function openDeActivateSupplierConfirmDialog(subcategory: CategoryData) {
    myContext.setConfirmDialog({
      type: 'deActivate',
      open: true,
      title: t('DEACTIVATE_SUB_CATEGORY'),
      message: t('DEACTIVATE_SUB_CATEGORY_MESSAGE'),
      onNoClick: () => { },
      onYesClick: (event: any, param: string | undefined | boolean) => {
        updateSubCategoryStatus(subcategory, 'Inactive');
      }
    });
  }

  const updateSubCategoryStatus = async (subcategory: CategoryData, status: string) => {
    try {
      const payload = {
        id: subcategory?._id,
        categoryId: subcategory?.categoryId,
        subCategoryName: subcategory?.subCategoryName,
        description: subcategory?.description,
        status
      };

      await dispatch(modifySubCategory({ ...payload }));
      await refreshPageData(pageNo - 1, search, filter);
    } catch (error) {
      ShowSnackbarFailure(error, myContext);
    }
  };

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  const refreshPageData = async (newPage: number, search?: string, filter?: {} | undefined) => {
    let payload = await preparePayload({ limit: rowsPerPage }, filter, search, newPage);
    if (categoryId) {
      payload = { ...payload, categoryId };
    }

    await dispatch(fetchSubCategories(payload));
    // await fetchData(fetchSubCategories, payload);

  };

  function SubCategoriesUI() {
    return (
      <Grid>
        <Grid item>
          <BakeasyAppBar
            title={`${location?.state?.name}`}
            menus={
              <>
                <div className="tableOptions">
                  <Button.Print
                    onClick={function(
                      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                    ): void {
                      dispatch(downloadSubCategories({ ...filter, downloadType: 'pdf' }));
                    }}
                  />
                  <Button.Export
                    onClick={function (
                      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                    ): void {
                      dispatch(downloadSubCategories({ ...filter, downloadType: 'csv' }));
                    }}
                  />

                  <BakeasyButtonAdd
                    onClick={function (
                      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                    ): void {
                      openAddEditDialog(false, { categoryId: categoryId, name: '', status: '' });
                    }}
                  />
                </div>
              </>
            }
            onBackClick={() => {
              navigate(-1);
            }}
          />
        </Grid>

        <Paper component={Card} variant="outlined" sx={{ p: 2, mt: 1 }}>
          <Grid container spacing={2} sx={{ display: 'flex' }}>
            <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (event?.target?.value?.length === 0 || event?.target?.value?.length > 2) {
                    await refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0 ? undefined : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  await refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={async (event: React.KeyboardEvent<HTMLInputElement>) => {
                  await refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <BakeasySelect
                disabled={false}
                label={t('STATUS')}
                size="small"
                fullWidth={true}
                statusOptions={STATUS_ALL_ARRAY1}
                onChange={async (e: any) => {
                  let payload: any = { status: e?.target?.value === 'All' ? '' : e?.target?.value };
                  await refreshPageData(pageNo - 1, search, payload);
                }}
              />
            </Grid>
          </Grid>

          <Grid>
            {subCategoryList?.length > 0 ? (
              <BakeasyTable
                headers={subCategoriesTableHeaders}
                tableData={subCategoryList}
                ref={tableRef}
                count={count}
                pageNo={pageNo - 1}
                search={search}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </Paper>
      </Grid>
    );
  }

  return SubCategoriesUI();
};
